import type { MessageKey } from "./i18n";
import type {
  LinkAction,
  CalendarProvider,
  LinkCategory,
  LinkIconKey,
  LinkInputMode,
  LinkKind,
  LinkType,
  ProfileLink,
  ValidationIssue
} from "./types";

export const LINK_LIMIT = 8;

type LinkTypeConfig = {
  type: LinkType;
  label: string;
  category: LinkCategory;
  icon: LinkIconKey;
  inputMode: LinkInputMode;
  inputLabel: string;
  placeholder: string;
  action: LinkAction;
  singleton: boolean;
  normalize?: (value: string) => string;
  validate: (value: string) => string | null;
  buildUrl?: (value: string) => string;
};

type CalendarProviderConfig = {
  provider: CalendarProvider;
  label: string;
  inputMode: "slug" | "url";
  inputLabel: string;
  placeholder: string;
  normalize: (value: string) => string;
  validate: (value: string) => string | null;
  buildUrl: (value: string) => string;
};

const HTTPS_RE = /^https:\/\/[^\s]+$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const GENERIC_HANDLE_RE = /^[a-zA-Z0-9._-]{1,64}$/;
const SCHEDULING_SLUG_RE = /^[a-zA-Z0-9][a-zA-Z0-9._~/-]{0,95}$/;

export const ENABLED_LINK_TYPES: LinkType[] = [
  "x",
  "github",
  "linkedin",
  "instagram",
  "youtube",
  "bilibili",
  "xiaohongshu",
  "wechat",
  "website",
  "email",
  "calendar"
];
export const LINK_CATEGORY_ORDER: LinkCategory[] = ["social", "booking", "contact", "work", "content", "other"];
export const LINK_CATEGORY_LABELS: Record<LinkCategory, string> = {
  social: "社交",
  booking: "预约",
  work: "作品 / 主页",
  content: "内容",
  contact: "联系",
  other: "其他"
};

const LINK_TYPE_LABEL_KEYS: Record<LinkType, MessageKey> = {
  x: "links.type.x",
  github: "links.type.github",
  linkedin: "links.type.linkedin",
  instagram: "links.type.instagram",
  youtube: "links.type.youtube",
  bilibili: "links.type.bilibili",
  xiaohongshu: "links.type.xiaohongshu",
  wechat: "links.type.wechat",
  website: "links.type.website",
  newsletter: "links.type.newsletter",
  calendar: "links.type.calendar",
  email: "links.type.email",
  tiktok: "links.type.tiktok",
  threads: "links.type.threads",
  mastodon: "links.type.mastodon",
  dribbble: "links.type.dribbble",
  jike: "links.type.jike",
  other: "links.type.other"
};

const LINK_INPUT_LABEL_KEYS: Record<LinkType, MessageKey> = {
  x: "links.input.x",
  github: "links.input.github",
  linkedin: "links.input.linkedin",
  instagram: "links.input.instagram",
  youtube: "links.input.youtube",
  bilibili: "links.input.bilibili",
  xiaohongshu: "links.input.xiaohongshu",
  wechat: "links.input.wechat",
  website: "links.input.website",
  newsletter: "links.input.newsletter",
  calendar: "links.input.calendar",
  email: "links.input.email",
  tiktok: "links.input.tiktok",
  threads: "links.input.threads",
  mastodon: "links.input.mastodon",
  dribbble: "links.input.dribbble",
  jike: "links.input.jike",
  other: "links.input.other"
};

const CALENDAR_INPUT_LABEL_KEYS: Record<CalendarProvider, MessageKey> = {
  cal: "links.input.calendar.cal",
  calendly: "links.input.calendar.calendly",
  google: "links.input.calendar.google",
  microsoft: "links.input.calendar.microsoft",
  hubspot: "links.input.calendar.hubspot",
  savvycal: "links.input.calendar.savvycal",
  tidycal: "links.input.calendar.tidycal",
  zcal: "links.input.calendar.zcal"
};

export const DEFAULT_CALENDAR_PROVIDER: CalendarProvider = "cal";

export const CALENDAR_PROVIDER_CONFIG: Record<CalendarProvider, CalendarProviderConfig> = {
  cal: {
    provider: "cal",
    label: "Cal.com",
    inputMode: "slug",
    inputLabel: "Cal.com 路径",
    placeholder: "yourname/intro",
    normalize: normalizeSchedulingSlug,
    validate: validateSchedulingSlug,
    buildUrl: (value) => `https://cal.com/${value}`
  },
  calendly: {
    provider: "calendly",
    label: "Calendly",
    inputMode: "slug",
    inputLabel: "Calendly 路径",
    placeholder: "yourname/intro",
    normalize: normalizeSchedulingSlug,
    validate: validateSchedulingSlug,
    buildUrl: (value) => `https://calendly.com/${value}`
  },
  google: {
    provider: "google",
    label: "Google Calendar",
    inputMode: "url",
    inputLabel: "Google Calendar 预约页",
    placeholder: "https://calendar.app.google/...",
    normalize: normalizeUrl,
    validate: (value) => validateAllowedHostUrl(value, ["calendar.app.google", "calendar.google.com"], "请输入有效的 Google Calendar 预约链接。"),
    buildUrl: (value) => value
  },
  microsoft: {
    provider: "microsoft",
    label: "Microsoft Bookings",
    inputMode: "url",
    inputLabel: "Microsoft Bookings 链接",
    placeholder: "https://outlook.office365.com/owa/calendar/...",
    normalize: normalizeUrl,
    validate: (value) =>
      validateAllowedHostUrl(
        value,
        ["book.ms", "bookings.office.com", "outlook.office.com", "outlook.office365.com"],
        "请输入有效的 Microsoft Bookings 链接。"
      ),
    buildUrl: (value) => value
  },
  hubspot: {
    provider: "hubspot",
    label: "HubSpot Meetings",
    inputMode: "url",
    inputLabel: "HubSpot Meetings 链接",
    placeholder: "https://meetings.hubspot.com/yourname",
    normalize: normalizeUrl,
    validate: (value) => validateAllowedHostUrl(value, ["meetings.hubspot.com"], "请输入有效的 HubSpot Meetings 链接。"),
    buildUrl: (value) => value
  },
  savvycal: {
    provider: "savvycal",
    label: "SavvyCal",
    inputMode: "slug",
    inputLabel: "SavvyCal 路径",
    placeholder: "yourname/intro",
    normalize: normalizeSchedulingSlug,
    validate: validateSchedulingSlug,
    buildUrl: (value) => `https://savvycal.com/${value}`
  },
  tidycal: {
    provider: "tidycal",
    label: "TidyCal",
    inputMode: "slug",
    inputLabel: "TidyCal 路径",
    placeholder: "yourname/intro",
    normalize: normalizeSchedulingSlug,
    validate: validateSchedulingSlug,
    buildUrl: (value) => `https://tidycal.com/${value}`
  },
  zcal: {
    provider: "zcal",
    label: "zcal",
    inputMode: "slug",
    inputLabel: "zcal 路径",
    placeholder: "yourname/intro",
    normalize: normalizeSchedulingSlug,
    validate: validateSchedulingSlug,
    buildUrl: (value) => `https://zcal.co/${value}`
  }
};

export const CALENDAR_PROVIDERS: CalendarProvider[] = ["cal", "calendly", "google", "microsoft", "hubspot", "savvycal", "tidycal", "zcal"];

export const LINK_TYPE_CONFIG: Record<LinkType, LinkTypeConfig> = {
  x: {
    type: "x",
    label: "X",
    category: "social",
    icon: "x",
    inputMode: "handle",
    inputLabel: "X handle",
    placeholder: "yourname",
    action: "open_url",
    singleton: true,
    normalize: normalizeHandle,
    validate: validateGenericHandle,
    buildUrl: (value) => `https://x.com/${value}`
  },
  github: {
    type: "github",
    label: "GitHub",
    category: "social",
    icon: "github",
    inputMode: "handle",
    inputLabel: "GitHub handle",
    placeholder: "yourname",
    action: "open_url",
    singleton: true,
    normalize: normalizeHandle,
    validate: (value) => (/^[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,37}[a-zA-Z0-9])?$/.test(value) ? null : "请输入有效的 GitHub handle。"),
    buildUrl: (value) => `https://github.com/${value}`
  },
  linkedin: {
    type: "linkedin",
    label: "LinkedIn",
    category: "social",
    icon: "linkedin",
    inputMode: "url",
    inputLabel: "LinkedIn URL",
    placeholder: "https://www.linkedin.com/in/yourname",
    action: "open_url",
    singleton: true,
    normalize: normalizeUrl,
    validate: (value) => validateAllowedHostUrl(value, ["linkedin.com"], "请输入有效的 LinkedIn 链接。"),
    buildUrl: (value) => value
  },
  instagram: {
    type: "instagram",
    label: "Instagram",
    category: "social",
    icon: "instagram",
    inputMode: "handle",
    inputLabel: "Instagram handle",
    placeholder: "yourname",
    action: "open_url",
    singleton: true,
    normalize: normalizeHandle,
    validate: validateGenericHandle,
    buildUrl: (value) => `https://instagram.com/${value}`
  },
  youtube: {
    type: "youtube",
    label: "YouTube",
    category: "social",
    icon: "youtube",
    inputMode: "handle",
    inputLabel: "YouTube handle",
    placeholder: "yourname",
    action: "open_url",
    singleton: true,
    normalize: normalizeHandle,
    validate: validateGenericHandle,
    buildUrl: (value) => `https://youtube.com/@${value}`
  },
  bilibili: {
    type: "bilibili",
    label: "Bilibili",
    category: "social",
    icon: "bilibili",
    inputMode: "uid",
    inputLabel: "Bilibili UID",
    placeholder: "123456",
    action: "open_url",
    singleton: true,
    normalize: (value) => value.trim(),
    validate: (value) => (/^\d{1,20}$/.test(value) ? null : "请输入有效的 Bilibili UID。"),
    buildUrl: (value) => `https://space.bilibili.com/${value}`
  },
  xiaohongshu: {
    type: "xiaohongshu",
    label: "小红书",
    category: "social",
    icon: "xiaohongshu",
    inputMode: "url",
    inputLabel: "小红书主页 URL",
    placeholder: "https://www.xiaohongshu.com/user/profile/...",
    action: "open_url",
    singleton: true,
    normalize: normalizeUrl,
    validate: (value) => validateAllowedHostUrl(value, ["xiaohongshu.com"], "请输入有效的小红书主页链接。"),
    buildUrl: (value) => value
  },
  wechat: {
    type: "wechat",
    label: "微信公众号",
    category: "contact",
    icon: "wechat",
    inputMode: "text",
    inputLabel: "公众号 ID",
    placeholder: "youraccount",
    action: "copy",
    singleton: true,
    normalize: (value) => value.trim(),
    validate: (value) => (value.trim().length > 0 ? null : "请输入公众号 ID。")
  },
  website: {
    type: "website",
    label: "Website",
    category: "work",
    icon: "website",
    inputMode: "url",
    inputLabel: "网站 URL",
    placeholder: "https://example.com",
    action: "open_url",
    singleton: false,
    normalize: normalizeUrl,
    validate: validateHttpsUrl,
    buildUrl: (value) => value
  },
  newsletter: {
    type: "newsletter",
    label: "Newsletter",
    category: "content",
    icon: "newsletter",
    inputMode: "url",
    inputLabel: "订阅 URL",
    placeholder: "https://newsletter.example.com",
    action: "open_url",
    singleton: true,
    normalize: normalizeUrl,
    validate: validateHttpsUrl,
    buildUrl: (value) => value
  },
  calendar: {
    type: "calendar",
    label: "预约",
    category: "booking",
    icon: "calendar",
    inputMode: "slug",
    inputLabel: "预约路径",
    placeholder: "yourname/intro",
    action: "open_url",
    singleton: true,
    normalize: normalizeSchedulingSlug,
    validate: (value) => validateCalendarValue(DEFAULT_CALENDAR_PROVIDER, value),
    buildUrl: (value) => buildCalendarUrl(DEFAULT_CALENDAR_PROVIDER, value)
  },
  email: {
    type: "email",
    label: "Email",
    category: "contact",
    icon: "email",
    inputMode: "email",
    inputLabel: "邮箱",
    placeholder: "you@example.com",
    action: "mailto",
    singleton: true,
    normalize: (value) => value.trim().toLowerCase(),
    validate: (value) => (EMAIL_RE.test(value) ? null : "请输入有效邮箱。"),
    buildUrl: (value) => `mailto:${value}`
  },
  tiktok: {
    type: "tiktok",
    label: "TikTok",
    category: "social",
    icon: "tiktok",
    inputMode: "handle",
    inputLabel: "TikTok handle",
    placeholder: "yourname",
    action: "open_url",
    singleton: true,
    normalize: normalizeHandle,
    validate: validateGenericHandle,
    buildUrl: (value) => `https://www.tiktok.com/@${value}`
  },
  threads: {
    type: "threads",
    label: "Threads",
    category: "social",
    icon: "threads",
    inputMode: "handle",
    inputLabel: "Threads handle",
    placeholder: "yourname",
    action: "open_url",
    singleton: true,
    normalize: normalizeHandle,
    validate: validateGenericHandle,
    buildUrl: (value) => `https://www.threads.net/@${value}`
  },
  mastodon: {
    type: "mastodon",
    label: "Mastodon",
    category: "social",
    icon: "mastodon",
    inputMode: "url",
    inputLabel: "Mastodon URL",
    placeholder: "https://mastodon.social/@yourname",
    action: "open_url",
    singleton: true,
    normalize: normalizeUrl,
    validate: validateHttpsUrl,
    buildUrl: (value) => value
  },
  dribbble: {
    type: "dribbble",
    label: "Dribbble",
    category: "work",
    icon: "dribbble",
    inputMode: "handle",
    inputLabel: "Dribbble handle",
    placeholder: "yourname",
    action: "open_url",
    singleton: true,
    normalize: normalizeHandle,
    validate: validateGenericHandle,
    buildUrl: (value) => `https://dribbble.com/${value}`
  },
  jike: {
    type: "jike",
    label: "即刻",
    category: "social",
    icon: "jike",
    inputMode: "url",
    inputLabel: "即刻主页 URL",
    placeholder: "https://web.okjike.com/u/...",
    action: "open_url",
    singleton: true,
    normalize: normalizeUrl,
    validate: validateHttpsUrl,
    buildUrl: (value) => value
  },
  other: {
    type: "other",
    label: "Other",
    category: "other",
    icon: "other",
    inputMode: "url",
    inputLabel: "链接 URL",
    placeholder: "https://example.com",
    action: "open_url",
    singleton: false,
    normalize: normalizeUrl,
    validate: validateHttpsUrl,
    buildUrl: (value) => value
  }
};

export function createProfileLink({
  id,
  type,
  value,
  label,
  provider,
  position
}: {
  id: string;
  type: LinkType;
  value: string;
  label?: string;
  provider?: CalendarProvider;
  position: number;
}): ProfileLink {
  const config = LINK_TYPE_CONFIG[type];
  const calendarProvider = type === "calendar" ? provider ?? DEFAULT_CALENDAR_PROVIDER : undefined;
  const normalizedValue = normalizeLinkValue(type, value, calendarProvider);
  const url = type === "calendar" && calendarProvider ? buildCalendarUrl(calendarProvider, normalizedValue) : config.buildUrl?.(normalizedValue);

  return {
    id,
    type,
    category: config.category,
    label: (label || config.label).trim(),
    value: normalizedValue,
    action: config.action,
    position,
    ...(calendarProvider ? { provider: calendarProvider } : {}),
    ...(url ? { url } : {})
  };
}

export function getLinkTypeLabelKey(type: LinkType): MessageKey {
  return LINK_TYPE_LABEL_KEYS[type];
}

export function getLinkInputLabelKey(type: LinkType, provider?: CalendarProvider): MessageKey {
  if (type === "calendar") return getCalendarInputLabelKey(provider);
  return LINK_INPUT_LABEL_KEYS[type];
}

export function getCalendarInputLabelKey(provider: CalendarProvider | undefined): MessageKey {
  return CALENDAR_INPUT_LABEL_KEYS[normalizeCalendarProvider(provider) ?? DEFAULT_CALENDAR_PROVIDER];
}

export function normalizeLinkValue(type: LinkType, value: string, provider?: CalendarProvider): string {
  if (type === "calendar") return getCalendarProviderConfig(provider).normalize(value);
  return (LINK_TYPE_CONFIG[type].normalize ?? ((nextValue: string) => nextValue.trim()))(value);
}

export function validateProfileLink(link: ProfileLink): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const config = LINK_TYPE_CONFIG[link.type] ?? LINK_TYPE_CONFIG.other;

  if (!isLinkTypeEnabled(config.type)) {
    issues.push({
      field: `links.${link.id}.type`,
      message: "暂不支持此链接类型，请选择网站、平台或预约链接。",
      severity: "error",
      code: "validation.link.unsupportedType"
    });
  }

  if (!link.label.trim()) {
    issues.push({ field: `links.${link.id}.label`, message: "链接名称不能为空。", severity: "error", code: "validation.link.labelRequired" });
  }

  const calendarProvider = config.type === "calendar" ? normalizeCalendarProvider(link.provider) : undefined;
  if (config.type === "calendar" && !calendarProvider) {
    issues.push({ field: `links.${link.id}.provider`, message: "请选择预约平台。", severity: "error", code: "validation.link.providerRequired" });
  }

  const value = normalizeLinkValue(config.type, link.value, calendarProvider);
  const inputLabelKey = getLinkInputLabelKey(config.type, calendarProvider);
  const valueIssue = value
    ? config.type === "calendar" && calendarProvider
      ? validateCalendarValue(calendarProvider, value)
      : config.validate(value)
    : "链接内容不能为空。";
  if (valueIssue) {
    issues.push({
      field: `links.${link.id}.value`,
      message: valueIssue,
      severity: "error",
      code: value ? getLinkValidationCode(valueIssue) : "validation.link.valueRequired",
      params: { labelKey: inputLabelKey }
    });
  }

  return issues;
}

function getLinkValidationCode(message: string): string {
  if (message.includes("GitHub")) return "validation.link.validGithub";
  if (message.includes("Bilibili")) return "validation.link.validBilibili";
  if (message.includes("小红书")) return "validation.link.validXiaohongshu";
  if (message.includes("Google Calendar")) return "validation.link.validGoogleCalendar";
  if (message.includes("Microsoft Bookings")) return "validation.link.validMicrosoftBookings";
  if (message.includes("HubSpot Meetings")) return "validation.link.validHubSpot";
  if (message.includes("邮箱")) return "validation.link.validEmail";
  if (message.includes("https://")) return "validation.link.httpsOnly";
  return "validation.link.invalid";
}

export function resolveProfileLink(link: ProfileLink): { action: LinkAction; href?: string; copyText?: string; label: string } {
  const config = LINK_TYPE_CONFIG[link.type] ?? LINK_TYPE_CONFIG.other;
  const calendarProvider = config.type === "calendar" ? normalizeCalendarProvider(link.provider) ?? DEFAULT_CALENDAR_PROVIDER : undefined;
  const value = normalizeLinkValue(config.type, link.value, calendarProvider);
  if (config.action === "copy") return { action: "copy", copyText: value, label: link.label || config.label };
  const href = link.url || (config.type === "calendar" && calendarProvider ? buildCalendarUrl(calendarProvider, value) : config.buildUrl?.(value));
  return { action: config.action, href, label: link.label || config.label };
}

export function sortProfileLinks(links: ProfileLink[]): ProfileLink[] {
  return [...links].sort((left, right) => left.position - right.position);
}

export function isSingleInstanceLinkType(type: LinkType): boolean {
  return LINK_TYPE_CONFIG[type].singleton;
}

export function canAddLinkType(links: ProfileLink[], type: LinkType, editingId?: string): boolean {
  if (!isSingleInstanceLinkType(type)) return true;
  return !links.some((link) => link.type === type && link.id !== editingId);
}

export function isLinkTypeEnabled(type: LinkType): boolean {
  return ENABLED_LINK_TYPES.includes(type);
}

export function groupProfileLinksByCategory(links: ProfileLink[]): Array<{ category: LinkCategory; links: ProfileLink[] }> {
  const sorted = sortProfileLinks(links);
  return LINK_CATEGORY_ORDER.map((category) => ({
    category,
    links: sorted.filter((link) => link.category === category)
  })).filter((group) => group.links.length > 0);
}

export function hydrateProfileLinks(links: unknown[] = []): ProfileLink[] {
  return links
    .map((link, index) => hydrateProfileLink(link, index))
    .filter((link): link is ProfileLink => Boolean(link))
    .sort((left, right) => left.position - right.position)
    .map((link, position) => ({ ...link, position }));
}

function hydrateProfileLink(rawLink: unknown, fallbackPosition: number): ProfileLink | null {
  if (!rawLink || typeof rawLink !== "object") return null;
  const link = rawLink as Partial<ProfileLink> & { kind?: LinkKind; url?: string };
  const id = typeof link.id === "string" && link.id ? link.id : `link_${fallbackPosition}`;
  const position = typeof link.position === "number" ? link.position : fallbackPosition;

  if (link.type && LINK_TYPE_CONFIG[link.type]) {
    return createProfileLink({
      id,
      type: link.type,
      value: typeof link.value === "string" ? link.value : link.url ?? "",
      label: typeof link.label === "string" ? link.label : undefined,
      provider: normalizeCalendarProvider(link.provider) ?? (link.type === "calendar" ? inferCalendarProviderFromValue(link.value ?? link.url ?? "") : undefined),
      position
    });
  }

  return migrateLegacyLink(link, id, position);
}

function migrateLegacyLink(link: Partial<ProfileLink> & { kind?: LinkKind; url?: string }, id: string, position: number): ProfileLink {
  const legacyKind = link.kind ?? "website";
  const legacyUrl = link.url ?? "";
  const label = typeof link.label === "string" ? link.label : undefined;
  const parsed = inferTypeAndValueFromLegacy(legacyKind, legacyUrl);
  return createProfileLink({ id, type: parsed.type, value: parsed.value, label, provider: parsed.provider, position });
}

function inferTypeAndValueFromLegacy(kind: LinkKind, url: string): { type: LinkType; value: string; provider?: CalendarProvider } {
  if (kind === "email") return { type: "email", value: url.replace(/^mailto:/i, "") };
  if (kind === "github") {
    const handle = parseHandleFromUrl(url, ["github.com"]);
    return handle ? { type: "github", value: handle } : { type: "website", value: url };
  }
  if (kind === "x") {
    const handle = parseHandleFromUrl(url, ["x.com", "twitter.com"]);
    return handle ? { type: "x", value: handle } : { type: "website", value: url };
  }
  if (kind === "calendar") return { type: "calendar", value: url, provider: inferCalendarProviderFromValue(url) };
  if (kind === "newsletter") return { type: kind, value: url };
  if (kind === "other") return { type: "other", value: url };
  return { type: "website", value: url };
}

export function getCalendarProviderConfig(provider?: CalendarProvider): CalendarProviderConfig {
  return CALENDAR_PROVIDER_CONFIG[normalizeCalendarProvider(provider) ?? DEFAULT_CALENDAR_PROVIDER];
}

export function normalizeCalendarProvider(provider?: string): CalendarProvider | undefined {
  return provider && provider in CALENDAR_PROVIDER_CONFIG ? (provider as CalendarProvider) : undefined;
}

function inferCalendarProviderFromValue(value: string): CalendarProvider | undefined {
  try {
    const host = new URL(value).hostname.toLowerCase().replace(/^www\./, "");
    return CALENDAR_PROVIDERS.find((provider) => {
      const config = CALENDAR_PROVIDER_CONFIG[provider];
      if (config.inputMode === "slug") return host === new URL(config.buildUrl("placeholder")).hostname;
      return config.validate(value) === null;
    });
  } catch {
    return undefined;
  }
}

function parseHandleFromUrl(value: string, hosts: string[]): string | null {
  try {
    const parsed = new URL(value);
    if (!hosts.includes(parsed.hostname.replace(/^www\./, ""))) return null;
    const [handle] = parsed.pathname.split("/").filter(Boolean);
    return handle ? normalizeHandle(handle) : null;
  } catch {
    return null;
  }
}

function normalizeHandle(value: string): string {
  return value.trim().replace(/^@+/, "");
}

function normalizeUrl(value: string): string {
  return value.trim();
}

function normalizeSchedulingSlug(value: string): string {
  return value.trim().replace(/^https?:\/\/[^/]+\//, "").replace(/^\/+/, "").replace(/\/+$/, "");
}

function validateGenericHandle(value: string): string | null {
  return GENERIC_HANDLE_RE.test(value) ? null : "请输入有效 handle。";
}

function validateHttpsUrl(value: string): string | null {
  if (!HTTPS_RE.test(value)) return "链接只支持 https://。";
  try {
    const url = new URL(value);
    return url.protocol === "https:" && url.hostname ? null : "请输入有效链接。";
  } catch {
    return "请输入有效链接。";
  }
}

function validateAllowedHostUrl(value: string, allowedHosts: string[], message: string): string | null {
  const httpsIssue = validateHttpsUrl(value);
  if (httpsIssue) return httpsIssue;
  try {
    const host = new URL(value).hostname.toLowerCase().replace(/^www\./, "");
    return allowedHosts.some((allowedHost) => host === allowedHost || host.endsWith(`.${allowedHost}`)) ? null : message;
  } catch {
    return "请输入有效链接。";
  }
}

function validateSchedulingSlug(value: string): string | null {
  return SCHEDULING_SLUG_RE.test(value) && !value.includes("//") ? null : "请输入有效的预约路径。";
}

function validateCalendarValue(provider: CalendarProvider, value: string): string | null {
  return CALENDAR_PROVIDER_CONFIG[provider].validate(value);
}

function buildCalendarUrl(provider: CalendarProvider, value: string): string {
  return CALENDAR_PROVIDER_CONFIG[provider].buildUrl(value);
}
